import { useState } from 'react'
import { getConfigIssues } from '@/config/appConfig'
import { useAuthFlow } from '@/hooks/useAuthFlow'
import { useCopilotChat } from '@/hooks/useCopilotChat'
import { LoginScreen } from '@/features/security/LoginScreen'
import { ConfigErrorScreen } from '@/features/security/ConfigErrorScreen'
import { LoadingState } from '@/components/common/LoadingState'
import { AppShell } from '@/components/layout/AppShell'
import { UserProfileProvider } from '@/app/UserProfileContext'
import { ChatWorkspace } from '@/features/chat/ChatWorkspace'
import { IncidentsPanel } from '@/features/panels/IncidentsPanel'
import { IdentityPanel } from '@/features/panels/IdentityPanel'
import { EndpointsPanel } from '@/features/panels/EndpointsPanel'
import { AttackPathPanel } from '@/features/panels/AttackPathPanel'
import { EvidencePanel } from '@/features/panels/EvidencePanel'
import { RiskAssessmentPanel } from '@/features/panels/RiskAssessmentPanel'
import { CapabilitiesPanel } from '@/features/panels/CapabilitiesPanel'
import { AboutPanel } from '@/features/panels/AboutPanel'
import type { ViewId } from '@/types/navigation'
import styles from './App.module.css'

export function App() {
  const configIssues = getConfigIssues()
  const { status: authStatus, errorMessage, signIn } = useAuthFlow()
  const chat = useCopilotChat()
  const [activeView, setActiveView] = useState<ViewId>('workspace')

  if (configIssues.length > 0) {
    return <ConfigErrorScreen issues={configIssues} />
  }

  if (authStatus === 'initializing') {
    return (
      <div className={styles.fullScreenCenter}>
        <LoadingState label="Se inițializează SOC-Copilot…" />
      </div>
    )
  }

  if (authStatus !== 'authenticated') {
    return <LoginScreen status={authStatus} errorMessage={errorMessage} onSignIn={signIn} />
  }

  const openWorkspace = () => setActiveView('workspace')

  const renderView = () => {
    switch (activeView) {
      case 'workspace':
        return <ChatWorkspace chat={chat} />
      case 'incidents':
        return <IncidentsPanel onOpenWorkspace={openWorkspace} />
      case 'identity':
        return <IdentityPanel onOpenWorkspace={openWorkspace} />
      case 'endpoints':
        return <EndpointsPanel onOpenWorkspace={openWorkspace} />
      case 'attack-path':
        return <AttackPathPanel onOpenWorkspace={openWorkspace} />
      case 'evidence':
        return <EvidencePanel onOpenWorkspace={openWorkspace} />
      case 'risk-assessment':
        return <RiskAssessmentPanel onOpenWorkspace={openWorkspace} />
      case 'capabilities':
        return <CapabilitiesPanel onOpenWorkspace={openWorkspace} />
      case 'about':
        return <AboutPanel onOpenWorkspace={openWorkspace} />
      default:
        return <ChatWorkspace chat={chat} />
    }
  }

  return (
    <UserProfileProvider>
      <AppShell activeView={activeView} onNavigate={setActiveView} connectionStatus={chat.status}>
        {renderView()}
      </AppShell>
    </UserProfileProvider>
  )
}
